import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import OrderDesign from './OrderDesign';
import MenuPaintedButton from './components/MenuPaintedButton';
import './SeasonEndDialog.css';
import { getLocalizedSeasonName } from './utils/honorHallSeasonLocalization';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { WELCOME_MENU_BUTTON_PATH } from './config/welcomeAssets';
import {
  SEASON_COMPLETE_BGM_FADE_MS,
  SEASON_COMPLETE_BGM_PATH,
  SEASON_COMPLETE_FRAME_PATH,
  SEASON_COMPLETE_REVEAL1_SFX,
  SEASON_COMPLETE_REVEAL2_SFX,
  SEASON_COMPLETE_TIMING,
} from './config/seasonCompleteAssets';
import { useMusic } from './hooks/useMusic';
import { getCachedAudio } from './hooks/musicCache';
import { playSoundEffect } from './hooks/useSoundEffects';
import { resolvePublicAssetBackgroundUrl } from './utils/resolvePublicAssetUrl';

const PHASE = {
  INTRO: 'intro',
  RANKS: 'ranks',
  TITLE: 'title',
  REWARDS: 'rewards',
};

function formatRank(rank) {
  if (rank == null) return '—';
  return `#${rank}`;
}

function hasTitleEarned(result) {
  return Boolean(result?.titleKey && result?.titleColor);
}

function hasSeasonReward(result) {
  return Boolean(result?.rewardOrderId && result?.rewardCopies > 0);
}

function nextPhaseAfterRanks(result) {
  if (hasTitleEarned(result)) return PHASE.TITLE;
  if (hasSeasonReward(result)) return PHASE.REWARDS;
  return null;
}

function nextPhaseAfterTitle(result) {
  if (hasSeasonReward(result)) return PHASE.REWARDS;
  return null;
}

function SeasonEndDialog({ isOpen, result, onAcknowledge }) {
  const { t, i18n } = useTranslation();
  const [rewardOrder, setRewardOrder] = useState(null);
  const [rewardFetchError, setRewardFetchError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [phaseVisible, setPhaseVisible] = useState(true);
  const [rankRevealCount, setRankRevealCount] = useState(0);
  const [showContinue, setShowContinue] = useState(false);
  const [bgmPath, setBgmPath] = useState(null);
  const timersRef = useRef([]);

  const frameBackground = useMemo(
    () => resolvePublicAssetBackgroundUrl(SEASON_COMPLETE_FRAME_PATH),
    []
  );

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  const switchPhase = useCallback((nextPhase, { sfx } = {}) => {
    setPhaseVisible(false);
    schedule(() => {
      setPhase(nextPhase);
      setPhaseVisible(true);
      if (sfx) {
        playSoundEffect(sfx);
      }
    }, SEASON_COMPLETE_TIMING.fadeMs);
  }, [schedule]);

  // BGM: Ashes Cleared2 while open, then menu theme after track ends or before dismiss.
  useEffect(() => {
    if (isOpen) {
      setBgmPath(SEASON_COMPLETE_BGM_PATH);
    } else {
      setBgmPath(null);
    }
  }, [isOpen]);

  const bgmLoops = bgmPath === MAIN_MENU_MUSIC_PATH;
  useMusic(bgmPath, 0, SEASON_COMPLETE_BGM_FADE_MS, bgmLoops);

  useEffect(() => {
    if (!isOpen || bgmPath !== SEASON_COMPLETE_BGM_PATH) return undefined;

    const audio = getCachedAudio(SEASON_COMPLETE_BGM_PATH);
    const onEnded = () => {
      setBgmPath(MAIN_MENU_MUSIC_PATH);
    };
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('ended', onEnded);
    };
  }, [isOpen, bgmPath]);

  const ensureMenuBgm = useCallback(() => {
    setBgmPath(MAIN_MENU_MUSIC_PATH);
  }, []);

  // Reward order fetch
  useEffect(() => {
    if (!isOpen || !result?.rewardOrderId || !(result.rewardCopies > 0)) {
      setRewardOrder(null);
      setRewardFetchError(null);
      return undefined;
    }

    let cancelled = false;
    setRewardFetchError(null);
    const langQuery = buildOrderDetailLangQuery(i18n.language);

    fetch(`${API_URL}/order/${result.rewardOrderId}/full?${langQuery}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `SeasonEndDialog: failed to load reward order ${result.rewardOrderId} (HTTP ${res.status})`
          );
        }
        return res.json();
      })
      .then((data) => {
        if (!data) {
          throw new Error(
            `SeasonEndDialog: empty reward order payload for order ${result.rewardOrderId}`
          );
        }
        if (!cancelled) {
          setRewardOrder(data);
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setRewardFetchError(err.message || String(err));
          setRewardOrder(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, result, i18n.language]);

  // Cinematic phase timeline — final content stays; Continue appears below the frame.
  useEffect(() => {
    if (!isOpen || !result) {
      clearTimers();
      setPhase(PHASE.INTRO);
      setPhaseVisible(true);
      setRankRevealCount(0);
      setShowContinue(false);
      return undefined;
    }

    clearTimers();
    setPhase(PHASE.INTRO);
    setPhaseVisible(false);
    setRankRevealCount(0);
    setShowContinue(false);
    playSoundEffect(SEASON_COMPLETE_REVEAL2_SFX);

    const { introMs, fadeMs, ranksHoldMs, titleMs, rewardsMs, reveal1LeadMs } =
      SEASON_COMPLETE_TIMING;

    // Intro visual after reveal2 lead-in
    schedule(() => {
      setPhaseVisible(true);
    }, reveal1LeadMs);

    const ranksStartAt = reveal1LeadMs + introMs;
    const ranksVisibleAt = ranksStartAt + fadeMs;
    const afterRanksAt = ranksVisibleAt + ranksHoldMs;

    schedule(() => {
      switchPhase(PHASE.RANKS);
    }, ranksStartAt);

    const afterRanksNext = nextPhaseAfterRanks(result);
    if (afterRanksNext) {
      schedule(() => {
        switchPhase(afterRanksNext, {
          sfx: SEASON_COMPLETE_REVEAL2_SFX,
        });
      }, afterRanksAt);
    } else {
      schedule(() => {
        setShowContinue(true);
      }, afterRanksAt);
    }

    let cursor = afterRanksAt + fadeMs;

    if (hasTitleEarned(result)) {
      const titleEnd = cursor + titleMs;
      const afterTitleNext = nextPhaseAfterTitle(result);
      if (afterTitleNext) {
        schedule(() => {
          switchPhase(afterTitleNext, { sfx: SEASON_COMPLETE_REVEAL2_SFX });
        }, titleEnd);
        cursor = titleEnd + fadeMs;
      } else {
        schedule(() => {
          setShowContinue(true);
        }, titleEnd);
      }
    }

    if (hasSeasonReward(result)) {
      schedule(() => {
        setShowContinue(true);
      }, cursor + rewardsMs);
    }

    return () => {
      clearTimers();
    };
  }, [isOpen, result, clearTimers, schedule, switchPhase]);

  // Stagger rank rows: reveal1 lead-in, then visual (boom lands on reveal)
  useEffect(() => {
    if (!isOpen || phase !== PHASE.RANKS || !phaseVisible) {
      return undefined;
    }

    const { rankIntervalMs, rankCount, reveal1LeadMs } = SEASON_COMPLETE_TIMING;
    const ids = [];

    for (let i = 0; i < rankCount; i += 1) {
      const soundAt = i * rankIntervalMs;
      const visualAt = soundAt + reveal1LeadMs;

      ids.push(
        setTimeout(() => {
          playSoundEffect(SEASON_COMPLETE_REVEAL1_SFX);
        }, soundAt)
      );
      ids.push(
        setTimeout(() => {
          setRankRevealCount(i + 1);
        }, visualAt)
      );
    }

    return () => {
      ids.forEach((id) => clearTimeout(id));
    };
  }, [isOpen, phase, phaseVisible]);

  const handleAcknowledge = async () => {
    if (!result?.id || acknowledging) return;
    ensureMenuBgm();
    setAcknowledging(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/honor-hall/season/acknowledge-end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ resultId: result.id }),
      });
      if (!res.ok) {
        throw new Error(`SeasonEndDialog acknowledge failed (HTTP ${res.status})`);
      }
      onAcknowledge();
    } catch (err) {
      console.error('SeasonEndDialog acknowledge failed', err);
    } finally {
      setAcknowledging(false);
    }
  };

  if (!isOpen || !result) return null;

  const ranks = result.ranks || {};
  const rankItems = [
    { key: 'battleLegends', rank: ranks.battleLegends },
    { key: 'battleStrategies', rank: ranks.battleStrategies },
    { key: 'warHeroes', rank: ranks.warHeroes },
    { key: 'explorers', rank: ranks.explorers },
    { key: 'assetStrategies', rank: ranks.assetStrategies },
  ];

  const titleLabel =
    result.titleKey === 'trailblazer'
      ? t('seasonEnd.dialog.trailblazer')
      : t('seasonEnd.dialog.legend');

  return (
    <div className="season-end-dialog-overlay" role="alertdialog" aria-modal="true">
      <div className="season-end-dialog-shell">
        <div
          className="season-end-dialog"
          style={{ backgroundImage: frameBackground }}
        >
          <div
            className={`season-end-dialog-stage${phaseVisible ? ' is-visible' : ' is-fading'}`}
          >
            {phase === PHASE.INTRO && (
              <div className="season-end-dialog-intro">
                <h2 className="season-end-dialog-shiny">{t('seasonEnd.dialog.title')}</h2>
                <p className="season-end-dialog-season season-end-dialog-shiny-sub">
                  {t('seasonEnd.dialog.seasonLine', {
                    number: result.seasonNumber,
                    name: getLocalizedSeasonName(t, result.seasonNumber),
                  })}
                </p>
              </div>
            )}

            {phase === PHASE.RANKS && (
              <ul className="season-end-dialog-ranks">
                {rankItems.map((item, index) => (
                  <li
                    key={item.key}
                    className={`season-end-dialog-rank-row${
                      index < rankRevealCount ? ' is-revealed' : ''
                    }`}
                  >
                    {t(`seasonEnd.dialog.${item.key}`, { rank: formatRank(item.rank) })}
                  </li>
                ))}
              </ul>
            )}

            {phase === PHASE.TITLE && hasTitleEarned(result) && (
              <p className="season-end-dialog-title season-end-dialog-title-reveal">
                {t('seasonEnd.dialog.titleEarned', {
                  title: titleLabel,
                  color: t(`seasonEnd.dialog.color.${result.titleColor}`),
                })}
              </p>
            )}

            {phase === PHASE.REWARDS && (
              <div className="season-end-dialog-rewards season-end-dialog-rewards-reveal">
                <div className="season-end-dialog-rewards-copy">
                  <p className="season-end-dialog-reward-text">
                    {t('seasonEnd.dialog.rewardReceived', { copies: result.rewardCopies || 0 })}
                  </p>
                  {rewardFetchError && (
                    <p className="season-end-dialog-reward-error" role="alert">
                      {rewardFetchError}
                    </p>
                  )}
                </div>
                {rewardOrder && result.rewardCopies > 0 && (
                  <div className="season-end-dialog-reward-card">
                    <div className="season-end-dialog-reward-card-scale">
                      <OrderDesign order={rewardOrder} noMargin />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className={`season-end-dialog-continue${showContinue ? ' is-visible' : ''}`}
        >
          <MenuPaintedButton
            className="season-end-dialog-ack"
            imagePath={WELCOME_MENU_BUTTON_PATH}
            onClick={handleAcknowledge}
            disabled={!showContinue || acknowledging}
          >
            {acknowledging
              ? t('seasonEnd.dialog.acknowledging')
              : t('seasonEnd.dialog.acknowledge')}
          </MenuPaintedButton>
        </div>
      </div>
    </div>
  );
}

export default SeasonEndDialog;
